// Consent RPCs — trusted wrappers.
// Supabase owns the consent version, the text, and acceptance authority.
// React never fabricates acceptance or hardcodes the version.

import { supabase, supabaseConfigured } from './supabase';

function logSanitized(label, err) {
  try {
    console.warn('[etff:consent]', label, {
      name: err?.name || null,
      code: err?.code || null,
      message: err?.message || null,
    });
  } catch { /* ignore */ }
}

// get_current_consent() — returns the current authoritative consent
// document plus (per contract) whether the current customer has
// already accepted it for the current order context.
export async function rpcGetCurrentConsent() {
  if (!supabaseConfigured || !supabase) {
    return { ok: false, data: null, error: 'Consent is temporarily unavailable.' };
  }
  try {
    const { data, error } = await supabase.rpc('get_current_consent');
    if (error) {
      logSanitized('get_current_consent:error', error);
      return { ok: false, data: null, error: 'We could not load the terms. Please try again.' };
    }
    return { ok: true, data, error: null };
  } catch (err) {
    logSanitized('get_current_consent:threw', err);
    return { ok: false, data: null, error: 'We could not load the terms. Please try again.' };
  }
}

// accept_current_consent() — records the customer's explicit acceptance
// of the current consent version. Called only after the customer has
// ticked the checkbox and clicked the primary action.
export async function rpcAcceptCurrentConsent() {
  if (!supabaseConfigured || !supabase) {
    return { ok: false, data: null, error: 'Consent is temporarily unavailable.' };
  }
  try {
    const { data, error } = await supabase.rpc('accept_current_consent');
    if (error) {
      logSanitized('accept_current_consent:error', error);
      return { ok: false, data: null, error: 'We could not record your acceptance. Please try again.' };
    }
    return { ok: true, data, error: null };
  } catch (err) {
    logSanitized('accept_current_consent:threw', err);
    return { ok: false, data: null, error: 'We could not record your acceptance. Please try again.' };
  }
}

// Tolerant readers.
export function readConsentText(payload) {
  if (!payload || typeof payload !== 'object') return null;
  for (const k of ['text', 'consent_text', 'body', 'content']) {
    const v = payload[k];
    if (typeof v === 'string' && v.length) return v;
  }
  return null;
}
export function readConsentVersion(payload) {
  if (!payload || typeof payload !== 'object') return null;
  for (const k of ['version', 'consent_version', 'current_version']) {
    const v = payload[k];
    if (v !== null && v !== undefined && String(v).length) return String(v);
  }
  return null;
}
export function readConsentAlreadyAccepted(payload) {
  if (!payload || typeof payload !== 'object') return false;
  for (const k of ['already_accepted', 'accepted', 'is_accepted']) {
    if (payload[k] === true) return true;
  }
  const outcome = payload.outcome;
  if (typeof outcome === 'string' && (outcome === 'already_accepted' || outcome === 'accepted')) return true;
  return false;
}
export function readConsentAcceptOutcome(payload) {
  if (!payload || typeof payload !== 'object') return null;
  const o = payload.outcome;
  return typeof o === 'string' ? o : null;
}
